/**
 * @category Tutorial
 * @module TutorialLoader
 * @description Utilities for loading and managing JSON-based tutorial definitions
 */

import tutorial1Raman from "../tutorialDefinitions/tutorial1-raman.json";
import tutorial2Reflectivity from "../tutorialDefinitions/tutorial2-reflectivity.json";
import tutorial3Data from "../tutorialDefinitions/tutorial3-dataloading.json";
import tutorial4Coating from "../tutorialDefinitions/tutorial4-simplecoating.json";
import { hasProperty } from "../../../../utils/helpers";

/**
 * Registry of available tutorials
 */
const TUTORIAL_REGISTRY = {
  tutorial1: tutorial1Raman,
  tutorial2: tutorial2Reflectivity,
  tutorial3: tutorial3Data,
  tutorial4: tutorial4Coating,
};

/**
 * Load a tutorial definition by ID
 * @param {string} tutorialId - The tutorial identifier
 * @returns {Object|null} Tutorial definition object or null if not found
 */
export const loadTutorial = (tutorialId) => {
  const tutorial = TUTORIAL_REGISTRY[tutorialId];
  if (!tutorial) {
    console.warn(`Tutorial '${tutorialId}' not found in registry`);
    return null;
  }

  return {
    ...tutorial,
    // Add computed properties
    totalSteps: tutorial.steps.length,
    stepMap: createStepMap(tutorial.steps),
  };
};

/**
 * Create a map of step IDs to step objects for quick lookup
 * @param {Array} steps - Array of tutorial steps
 * @returns {Object} Map of stepId -> step object
 */
const createStepMap = (steps) => {
  return steps.reduce((map, step) => {
    map[step.id] = step;
    map[step.stepNumber] = step; // Also allow lookup by step number
    return map;
  }, {});
};

/**
 * Get all available tutorial IDs
 * @returns {Array<string>} Array of tutorial identifiers
 */
export const getAvailableTutorials = () => {
  return Object.keys(TUTORIAL_REGISTRY);
};

/**
 * Get tutorial metadata without loading full definition
 * @param {string} tutorialId - The tutorial identifier
 * @returns {Object|null} Tutorial metadata or null if not found
 */
export const getTutorialMetadata = (tutorialId) => {
  const tutorial = TUTORIAL_REGISTRY[tutorialId];
  if (!tutorial) return null;

  return {
    id: tutorial.id,
    name: tutorial.name,
    description: tutorial.description,
    type: tutorial.type,
    ...tutorial.metadata,
    totalSteps: tutorial.steps.length,
  };
};

/**
 * Validate a tutorial definition
 * @param {Object} tutorial - Tutorial definition to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateTutorial = (tutorial) => {
  const errors = [];

  // Required fields
  if (!tutorial.id) errors.push("Tutorial must have an id");
  if (!tutorial.name) errors.push("Tutorial must have a name");
  if (!tutorial.steps || !Array.isArray(tutorial.steps)) {
    errors.push("Tutorial must have a steps array");
  }

  // Validate steps
  if (tutorial.steps) {
    tutorial.steps.forEach((step, index) => {
      if (!step.id) errors.push(`Step ${index + 1} must have an id`);
      if (!step.stepNumber)
        errors.push(`Step ${index + 1} must have a stepNumber`);
      if (!step.predicates || !Array.isArray(step.predicates)) {
        errors.push(`Step ${index + 1} must have predicates array`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Enhanced predicate evaluation system
 * Supports complex conditions and multiple valid actions per step
 */
export function evaluatePredicate(predicate, input, context = {}) {
  if (!predicate) return false;
 
  if (predicate.type !== input) return false;
  // Enhanced predicate object
  if (typeof predicate === "object") {
    return evaluatePredicateObject(predicate, input, context);
  }

  return false;
}

function evaluatePredicateObject(predicate, input, context) {
  // Single condition predicate
  if (hasProperty(predicate, "value") && hasProperty(predicate, "data")) {
    return evaluateSingleCondition(predicate, input, context);
  }

  // Multi-condition predicate with AND/OR logic
  if (hasProperty(predicate, "conditions") && predicate.conditions.length > 0) {
    if (hasProperty(predicate, "operator") && predicate.operator === "OR") {
      return predicate.conditions.some((condition) =>
        evaluateSingleCondition(condition, input, context)
      );
    } else {
      // AND (default)
      return predicate.conditions.every((condition) =>
        evaluateSingleCondition(condition, input, context)
      );
    }
  }

  return false;
}

function evaluateSingleCondition(condition, input, context) {
  if (condition === "true") {
    return true;
  }
  //console.log("Tutorial Debug:condition", condition);
  //console.log("Tutorial Debug:input", input);
  //console.log("Tutorial Debug:context", context);
  let response = evaluateConditionExpression(condition, context);
  //console.log("Tutorial Debug:response", response);
  return response;
}

/**
 * 
 * @param {string} expression - Condition expression to evaluate
 * @param {Object} context - Current context with stateChange data
 * @returns {boolean} True if condition is met
 */
function evaluateConditionExpression(expression, context) {
  if (!context.stateChange || !context.stateChange.data) return false;

  try {
  const data = context.stateChange.data;
  if (hasProperty(expression, "value") && hasProperty(expression, "data")) {
    let operator = "equals";
    if (hasProperty(expression, "operator")) {
      operator = expression.operator;
    }
    if (hasProperty(expression, "dataKey")) {
       if (Array.isArray(data[expression.data])) {
        return checkDataConditionObjectArray(
          expression.dataKey,
          data[expression.data],
          operator,
          expression.value
        );
      }
      if (
        typeof data[expression.data] === "object" &&
        hasProperty(data[expression.data], expression.dataKey)
      ) {
        return checkDataCondition(
          data[expression.data][expression.dataKey],
          operator,
          expression.value
        );
      }
      console.warn(
        "condition expression has dataKey but is not an array or object",
        expression
      );
      return false;
    }
    return checkDataCondition(
      data[expression.data],
      operator,
      expression.value
    );
  }
  } catch (error) {
    console.error(
      "Error evaluating condition expression: ",
      error
    );
    return false;
  }
    
}

// /**
//  * Check if context matches expected value
//  * @param {*} expectedValue - Expected value to match
//  * @param {Object} context - Current context state
//  * @returns {boolean} True if context matches expected value
//  */
// function checkContextValue(expectedValue, context) {
//   // Simple equality check for now
//   // TODO: Add more sophisticated matching (partial, regex, etc.)
//   return JSON.stringify(context.value) === JSON.stringify(expectedValue);
// }

function checkDataCondition(dataValue, operator, checkValue = null) {
  if (checkValue !== null) {
    if (typeof dataValue === "string") {
      if (typeof checkValue === "number") {
        dataValue = parseFloat(dataValue);
      }
      if (typeof checkValue === "boolean") {
        dataValue = dataValue === "true";
      }
    }
  }
  switch (operator) {
    case "equals":
      return dataValue === checkValue;
    case "contains":
      if (checkValue.includes("&&&&")) {
        let checkValueArray = checkValue.split("&&&&");
        let passed = true;
        for (const key in checkValueArray) {
          if (
            !checkDataCondition(dataValue, "contains", checkValueArray[key])
          ) {
            passed = false;
            break;
          }
        }
        return passed;
      }
      return (
        typeof dataValue === "string" &&
        dataValue.toLowerCase().includes(checkValue.toLowerCase())
      );
    case "starts_with":
      return (
        typeof dataValue === "string" &&
        dataValue.toLowerCase().startsWith(checkValue.toLowerCase())
      );
    case "ends_with":
      return (
        typeof dataValue === "string" &&
        dataValue.toLowerCase().endsWith(checkValue.toLowerCase())
      );
    case "greater_than":
      return typeof dataValue === "number" && dataValue > checkValue;
    case "less_than":
      return typeof dataValue === "number" && dataValue < checkValue;
    case "exists":
      return dataValue !== undefined && dataValue !== null;
    case "equalsObject":
      return checkDataObjectToValueObject(dataValue, operator, checkValue);
    case "containsObject":
      return checkDataObjectToValueObject(dataValue, operator, checkValue);
    case "length":
      return Array.isArray(dataValue) && dataValue.length === checkValue;
    default:
      return false;
  }
}

function checkDataConditionObjectArray(
  dataKey,
  dataValue,
  operator,
  checkValue = null
) {
  return dataValue.some(
    (item) =>
      hasProperty(item, dataKey) &&
      checkDataCondition(item[dataKey], operator, checkValue)
  );
}

function checkDataObjectToValueObject(dataValue, operator, checkValue) {
  if (operator === "equalsObject") {
    return (
      checkDataObjectToValueObject(dataValue, "containsObject", checkValue) &&
      checkDataObjectToValueObject(checkValue, "containsObject", dataValue)
    );
  }
  // Handle null/undefined cases
  if (
    dataValue === null ||
    dataValue === undefined ||
    checkValue === null ||
    checkValue === undefined
  ) {
    return dataValue === checkValue;
  }

  // Handle non-object types
  if (typeof checkValue !== "object") {
    if (typeof checkValue === "string") {
      return checkDataCondition(dataValue, "contains", checkValue);
    }
    return checkDataCondition(dataValue, "equals", checkValue);
  }

  if (typeof dataValue !== "object") {
    //console.log("Tutorial Debug:dataValue is not an object", dataValue);
    return false;
  }
  // Handle arrays
  if (Array.isArray(checkValue)) {
    if (!Array.isArray(dataValue)) {
      //console.log("Tutorial Debug:dataValue is not an array", dataValue);
      return false;
    }
    return checkDataArrayToValueArray(dataValue, operator, checkValue);
  }

  // For containsObject, check if dataValue contains all properties from checkValue
  for (const key in checkValue) {
    if (key.startsWith("Length&&&&")) {
      let checkLengthArray = key.split("&&&&")[1];
      if (
        !hasProperty(dataValue, checkLengthArray) ||
        !Array.isArray(dataValue[checkLengthArray]) ||
        dataValue[checkLengthArray].length !== checkValue[key]
      ) {
        //console.log("Tutorial Debug:checkLengthArray is not correct length",checkLengthArray, dataValue[checkLengthArray], checkValue[key]);
        return false;
      }
      continue;
    }
    if (key.startsWith("Index&&&&")) 
    {
      let checkIndex = key.split("&&&&")[1];
      if (
        !Array.isArray(dataValue) ||
        dataValue.length <= checkIndex ||
        !checkDataObjectToValueObject(dataValue[checkIndex],"containsObject", checkValue[key])
      ) {
        //console.log("Tutorial Debug:checkIndex is not correct", checkIndex, dataValue[checkIndex], checkValue[key]);
        return false;
      }
      continue;
    }
    if (!hasProperty(dataValue, key)) {
      //console.log("Tutorial Debug:dataValue does not have key", key, dataValue);
      return false;
    }
    if (
      !checkDataObjectToValueObject(dataValue[key], operator, checkValue[key])
    ) {
      //console.log("Tutorial Debug:dataValue[key] is not correct", dataValue[key], operator, checkValue[key]);
      return false;
    }
  }

  return true;
}

function checkDataArrayToValueArray(dataValue, operator, checkValue) {
  // Handle null/undefined cases
  if (
    dataValue === null ||
    dataValue === undefined ||
    checkValue === null ||
    checkValue === undefined
  ) {
    return dataValue === checkValue;
  }

  // Handle non-array types
  if (!Array.isArray(dataValue) || !Array.isArray(checkValue)) {
    return checkDataCondition(dataValue, "equals", checkValue);
  }

  // For containsObject, check if dataValue contains all elements from checkValue
  for (const checkItem of checkValue) {
    const found = dataValue.some((dataItem) => {
      if (typeof checkItem === "object" && checkItem !== null) {
        if (Array.isArray(checkItem)) {
          return (
            Array.isArray(dataItem) &&
            checkDataArrayToValueArray(dataItem, operator, checkItem)
          );
        }
        return checkDataObjectToValueObject(dataItem, operator, checkItem);
      }
      if (typeof checkItem === "string") {
        return checkDataCondition(dataItem, "contains", checkItem);
      }
      return checkDataCondition(dataItem, "equals", checkItem);
    });
    if (!found) {
      return false;
    }
  }
  return true;
}

/**
 * Evaluate all predicates for a tutorial step
 * @param {Array} predicates - Array of predicate objects
 * @param {string} input - Input action string
 * @param {Object} context - Current context state
 * @returns {boolean} True if any predicate matches (OR logic)
 */
export function evaluateStepPredicates(predicates, input, context = {}) {
  if (!predicates || !Array.isArray(predicates)) return false;

  // OR logic: any predicate can match to advance the step
  return predicates.some((predicate) =>
    evaluatePredicate(predicate, input, context)
  );
}

/**
 * Find next valid step based on current step and predicates
 * @param {Object} currentStep - Current tutorial step
 * @param {string} input - Input action string
 * @param {Object} context - Current context state
 * @returns {number|null} Next step number or null if no match
 */
export function findNextStep(currentStep, input, context = {}) {
  if (!currentStep || !currentStep.predicates) return null;

  // Check each predicate individually to find matching one with potential custom nextStep
  for (const predicate of currentStep.predicates) {
    if (evaluatePredicate(predicate, input, context)) {
      // If this predicate has a custom nextStep, use it
      if (predicate.nextStep) {
        return predicate.nextStep;
      }
      // Otherwise use the step's default nextStep

      return hasProperty(currentStep, "nextStep") ? currentStep.nextStep : null;
    }
  }

  return null;
}

export default {
  loadTutorial,
  getAvailableTutorials,
  getTutorialMetadata,
  validateTutorial,
  //createLegacyStepMapping,
  //legacyInputToPredicateType,
  evaluatePredicate,
  evaluateStepPredicates,
  findNextStep,
};
